import { env_url } from './endpoints'
import {
    getPlayerImageURL,
    getPlayerLink,
    getPlayerLinkWithPosition,
    formatNumberWithDecimal,
    calculatePercentage,
} from './shared_helpers.js'

export const NHLsilhouette = `${env_url}/player-silhouette-hockey-cut.png`

export const NHLLogo =
    'https://images.rogersdigitalmedia.com/www.sportsnet.ca/team_logos/200x200/hockey/nhl/nhl.png'

export const imgErrNHL = ev => {
    ev.target.src = NHLsilhouette
}

/*
 * formatPeriodName: This function returns the text label of a period based on it's numerical value
 *
 * Input: period - integer corresponding to a period
 * Input: game_type - string describing type of game. e.g. Preseason
 *
 * Output: period - string of text representing the period in plain english
 */
export const formatPeriodName = (period, game_type) => {
    if (period === 1) {
        return '1st'
    } else if (period === 2) {
        return '2nd'
    } else if (period === 3) {
        return '3rd'
    } else if (period === 4) {
        return 'OT'
    } else {
        if (game_type === 'Regular Season' || game_type === 'Preseason') {
            return 'SO'
        } else {
            // Post season game
            return period - 3 + 'OT'
        }
    }
}

/*
 * labelPeriods: This function adds text labels to the periods array
 *
 * Input: periods - array of period objects
 * Input: game_type - string describing type of game. e.g. Preseason
 *
 * Output: periods - array: the same array originally input, but with an added "label" paramter in each period object.
 */
export const labelPeriods = (periods, game_type, ighClips = []) => {
    let periodCopy = JSON.parse(JSON.stringify(periods))

    if (game_type === 'Regular Season' || game_type === 'Preseason') {
        periodCopy.forEach(function(period, i) {
            if (i < 3) {
                periodCopy[i].label = i + 1
            } else if (i === 3) {
                periodCopy[i].label = 'OT'
            } else {
                periodCopy[i].label = 'SO'
            }
        })
    } else {
        //Post season game
        periodCopy.forEach(function(period, i) {
            if (i < 3) {
                periodCopy[i].label = i + 1
            } else if (i === 3) {
                periodCopy[i].label = 'OT'
            } else {
                periodCopy[i].label = i - 2 + 'OT'
            }
        })
    }

    if (ighClips && ighClips.length !== 0) {
        periodCopy.forEach((period, p) => {
            let type = period.goals ? 'goals' : period.shootouts ? 'shootouts' : ''

            if (period.hasOwnProperty(type) && period[type].length !== 0) {
                period[type].forEach((item, g) => {
                    if (!isNaN(item.player_id)) {
                        const igh_length = ighClips.length

                        for (let i = 0; i < igh_length; i++) {
                            const ighClip = ighClips[i]

                            if (
                                ighClip.title &&
                                ighClip.keywords &&
                                ighClip.players &&
                                ighClip.players[0]
                            ) {
                                const regExpForParenthesesValue = /\(([^)]+)\)$/
                                const match = regExpForParenthesesValue.exec(ighClip.title)
                                const time_from_title = match[1]

                                if (time_from_title) {
                                    const colon_split = time_from_title.split(':')
                                    let igh_goal_minutes = null
                                    let igh_goal_seconds = null
                                    let is_igh_shootout = null

                                    if (
                                        type === 'goals' &&
                                        !isNaN(item.minutes) &&
                                        !isNaN(item.seconds)
                                    ) {
                                        igh_goal_minutes =
                                            colon_split[0] && parseInt(colon_split[0], 10)

                                        igh_goal_seconds =
                                            colon_split[1].split('/')[0] &&
                                            parseInt(colon_split[1].split('/')[0], 10)
                                    } else if (type === 'shootouts') {
                                        is_igh_shootout =
                                            colon_split[1].split('/')[1] &&
                                            colon_split[1].split('/')[1] === 'SO'
                                    }

                                    // Include a buffer of 3 seconds
                                    let match_with_buffer = false
                                    let wsc_total_seconds = item.minutes * 60 + item.seconds
                                    let igh_total_seconds = igh_goal_minutes * 60 + igh_goal_seconds
                                    if (Math.abs(wsc_total_seconds - igh_total_seconds) <= 3) {
                                        match_with_buffer = true
                                    }

                                    if (
                                        (type === 'goals' &&
                                            match_with_buffer &&
                                            ighClip.keywords === 'goal' &&
                                            item.player_id ===
                                                parseInt(ighClip.players[0][0], 10)) ||
                                        (type === 'shootouts' &&
                                            is_igh_shootout &&
                                            ((ighClip.keywords === 'goal' && item.is_goal) ||
                                                (ighClip.keywords === 'shot' && !item.is_goal)) &&
                                            item.player_id === parseInt(ighClip.players[0][0], 10))
                                    ) {
                                        periodCopy[p][type][g]['brightcove_video'] = {
                                            id: ighClip.brightcove_id || null,
                                            title: ighClip.title || '',
                                        }
                                    }
                                }
                            }
                        }
                    }
                })
            }
        })
    }

    return periodCopy
}

export const nhlTeamNameIDArrayMap = new Map([
    [4978, 'anaheim-ducks'],
    [4977, 'arizona-coyotes'],
    [4954, 'boston-bruins'],
    [4955, 'buffalo-sabres'],
    [4960, 'carolina-hurricanes'],
    [4956, 'calgary-flames'],
    [4957, 'chicago-blackhawks'],
    [4982, 'columbus-blue-jackets'],
    [4970, 'colorado-avalanche'],
    [4962, 'dallas-stars'],
    [4958, 'detroit-red-wings'],
    [4959, 'edmonton-oilers'],
    [4979, 'florida-panthers'],
    [4961, 'los-angeles-kings'],
    [4983, 'minnesota-wild'],
    [4963, 'montreal-canadiens'],
    [4964, 'new-jersey-devils'],
    [4980, 'nashville-predators'],
    [4965, 'new-york-islanders'],
    [4966, 'new-york-rangers'],
    [4967, 'ottawa-senators'],
    [4968, 'philadelphia-flyers'],
    [4969, 'pittsburgh-penguins'],
    [4971, 'san-jose-sharks'],
    [41971, 'seattle-kraken'],
    [4972, 'st-louis-blues'],
    [4973, 'tampa-bay-lightning'],
    [4974, 'toronto-maple-leafs'],
    [4975, 'vancouver-canucks'],
    [33447, 'vegas-golden-knights'],
    [4976, 'washington-capitals'],
    [4981, 'winnipeg-jets'],
])

export function formatLastNineEvents(lastNineEvents = [], homeTeam, visitingTeam) {
    const massagedPlays = lastNineEvents.map(play => {
        const formattedPlayDescription = play.description

        return {
            id: play.id,
            title: setPlayTitle(play),
            color: getPlayColor(play.event_id),
            teamColor: getTeamColor(play.team_id, homeTeam, visitingTeam),
            bodyText: shortenPlayerNames(formattedPlayDescription, play.players),
            teamLogo:
                play.team_id !== 0 ? getTeamLogo(play.team_id, homeTeam, visitingTeam) : NHLLogo,
            showPlayerHeadshot: play.player_id !== 0 ? true : false,
            playerHeadshot: setPlayerHeadshot(play.player_id),
            playerHeadshotError: imgErrNHL,
        }
    })

    const ghostPlays = [
        { id: 1, isGhost: true },
        { id: 2, isGhost: true },
        { id: 3, isGhost: true },
        { id: 4, isGhost: true },
        { id: 5, isGhost: true },
        { id: 6, isGhost: true },
        { id: 7, isGhost: true },
        { id: 8, isGhost: true },
        { id: 9, isGhost: true },
    ]

    return massagedPlays.concat(ghostPlays.slice(massagedPlays.length))
}

function setPlayTitle(play) {
    /* insert logic to rename events if needed */
    let formattedEvent = play
    let formattedPeriod = formatPeriodName(play.period)

    switch (play.event_id) {
        case 521:
            formattedEvent = 'Clear'
            break
        default:
            formattedEvent = play.event
    }

    formattedEvent = `${formattedEvent} - ${play.time} / ${formattedPeriod}`

    return formattedEvent || ''
}

function shortenPlayerNames(originalText, players = []) {
    let newText = originalText

    if (newText.includes('Stoppage')) {
        newText = newText
            .replace('Stoppage - ', '')
            .replace('-Home', '')
            .replace('-Away', '')
    }

    if (!players.length) {
        return newText
    }

    for (const player of players) {
        const textToReplace = `${player.first_name} ${player.last_name}`
        const playerInitial = player.first_name.charAt(0)
        const replacementText = `<b>${playerInitial}. ${player.last_name}</b>`

        newText = newText.replace(textToReplace, replacementText)
    }

    return newText
}

function getPlayColor(playTypeId) {
    const red = '#DA291C'
    const grey = '#636A71'
    const brightBlue = '#0D5EB8'

    const playColorMap = new Map([
        [502, grey],
        [503, red],
        [505, brightBlue],
        [506, grey],
        [507, grey],
        [508, grey],
        [509, red],
    ])

    return playColorMap.get(playTypeId) || grey
}

function setPlayerHeadshot(playerID) {
    return getPlayerImageURL(playerID, 'xs')
}

function getTeamLogo(teamID, homeTeam, visitingTeam) {
    return teamID === homeTeam.id
        ? homeTeam.image_url_90
        : teamID === visitingTeam.id
        ? visitingTeam.image_url_90
        : ''
}
function getTeamColor(teamID, homeTeam, visitingTeam) {
    return teamID === homeTeam.id
        ? homeTeam.color
        : teamID === visitingTeam.id
        ? visitingTeam.color
        : null
}

export function formatInjuries(teamInjuries = []) {
    const formattedInjuries = teamInjuries.map(injury => {
        return {
            playerName: getPlayerLink(injury),
            playerPosition: injury.short_position,
            status: `${injury.status.charAt(0).toUpperCase()}${injury.status.slice(1)}`,
            disabledListStatus: injury.disability_list_status,
            timestamp: injury.timestamp,
            emptyColumn: ' ',
        }
    })

    return formattedInjuries.sort((a, b) => b.timestamp - a.timestamp)
}

export function formatSeasonLeaders(homeTeam, visitingTeam) {
    const leaderCategories = [
        {
            title: 'Points',
            categoryKey: 'point_leader',
            valueKey: 'total',
        },
        {
            title: 'Goals',
            categoryKey: 'goal_leader',
            valueKey: 'total',
        },
        {
            title: 'Assists',
            categoryKey: 'assist_leader',
            valueKey: 'total',
        },
        {
            title: 'Wins',
            categoryKey: 'win_leader',
            valueKey: 'total',
        },
    ]

    const seasonLeaders = leaderCategories.map(leaderCategory => {
        const homeTeamData = homeTeam?.[leaderCategory.categoryKey]
        const visitingTeamData = visitingTeam?.[leaderCategory.categoryKey]

        if (!homeTeamData || !visitingTeamData) {
            return null
        }

        let homeTeamValue = homeTeamData[leaderCategory.valueKey]
        let visitingTeamValue = visitingTeamData[leaderCategory.valueKey]

        if (leaderCategory.alternateValueKey) {
            homeTeamValue = homeTeamValue || homeTeamData[leaderCategory.alternateValueKey]
            visitingTeamValue =
                visitingTeamValue || visitingTeamData[leaderCategory.alternateValueKey]
        }

        if (leaderCategory.decimalPlaces) {
            homeTeamValue = homeTeamValue.toFixed(leaderCategory.decimalPlaces)
            visitingTeamValue = visitingTeamValue.toFixed(leaderCategory.decimalPlaces)
        }

        return {
            title: leaderCategory.title,
            homeTeamShortName: homeTeam.short_name,
            homeTeamColor: homeTeam.color,
            visitingTeamColor: visitingTeam.color,
            visitingTeamShortName: visitingTeam.short_name,
            homeTeamPlayerImage: homeTeamData.image_urls.xs,
            visitingTeamPlayerImage: visitingTeamData.image_urls.xs,
            homeTeamLogo: homeTeam.image_url_90,
            visitingTeamLogo: visitingTeam.image_url_90,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            homeTeamPlayerName: `${homeTeamData.first_name.charAt(0)}. ${homeTeamData.last_name}`,
            visitingTeamPlayerName: `${visitingTeamData.first_name.charAt(0)}. ${
                visitingTeamData.last_name
            }`,
        }
    })
    return seasonLeaders.filter(n => n) // filter out null values
}

export function formatTeamStats(homeTeam, visitingTeam) {
    if (!visitingTeam.season_stats || !homeTeam.season_stats) {
        return []
    }

    const statCategories = [
        {
            title: 'Goals Per Game',
            valueKey: 'goals_per_game',
            valueFormatter: n => formatNumberWithDecimal(n, 2),
        },
        {
            title: 'Goals Against Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 2),
            valueKey: 'goals_against_per_game',
        },
        {
            title: 'Power Play Percentage',
            valueFormatter: n => `${formatNumberWithDecimal(n, 1)}`,
            valueKey: 'power_play_percentage',
            isPercent: true,
        },
        {
            title: 'Penalty Kill Percentage',
            valueFormatter: n => `${formatNumberWithDecimal(n, 1)}`,
            valueKey: 'penalty_kill',
            isPercent: true,
        },
    ]

    const teamStats = statCategories.map(statCategory => {
        let homeTeamValue = homeTeam.season_stats[statCategory.valueKey]
        let visitingTeamValue = visitingTeam.season_stats[statCategory.valueKey]

        if (
            (!homeTeamValue && homeTeamValue !== 0) ||
            (!visitingTeamValue && visitingTeamValue !== 0)
        ) {
            return null
        }

        return {
            title: statCategory.title,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            isPercent: statCategory.isPercent,
            valueFormatter: statCategory.valueFormatter,
        }
    })
    return teamStats.filter(n => n) // filter out null values
}

export function formatGameStats(homeTeam, visitingTeam) {
    if (!visitingTeam.game_stats || !homeTeam.game_stats) {
        return []
    }

    const statCategories = [
        {
            title: 'Shots on Goal',
            valueKey: 'shots',
        },
        {
            title: 'Hits',
            valueKey: 'hits',
        },
        {
            title: 'Faceoffs Won',
            valueKey: 'faceoffs',
        },
        {
            title: 'Penalty Minutes',
            valueKey: 'penalties_in_minutes',
        },
        {
            title: 'Power Plays',
            valueKey: 'powerplay',
        },
        {
            title: 'Giveaways',
            valueKey: 'turnover_giveaways',
        },
        {
            title: 'Takeaways',
            valueKey: 'blocks',
        },
    ]

    const gameStats = statCategories.map(statCategory => {
        let homeTeamValue = homeTeam.game_stats[statCategory.valueKey]
        let visitingTeamValue = visitingTeam.game_stats[statCategory.valueKey]

        if (
            (!homeTeamValue && homeTeamValue !== 0) ||
            (!visitingTeamValue && visitingTeamValue !== 0)
        ) {
            return null
        }

        return {
            title: statCategory.title,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            valueFormatter: statCategory.valueFormatter,
        }
    })
    return gameStats.filter(n => n) // filter out null values
}

export function formatSeasonRecord(team) {
    if (
        team?.season_stats?.wins &&
        team?.season_stats?.losses &&
        team?.season_stats?.overtime_losses
    ) {
        return `${team.season_stats.wins}-${team.season_stats.losses}-${team.season_stats.overtime_losses}`
    } else if (team?.season_stats?.wins && team?.season_stats?.losses) {
        return `${team.season_stats.wins}-${team.season_stats.losses}`
    } else {
        return ''
    }
}
export function formatShotCount(team) {
    if (team?.game_stats?.shots) {
        return `SOG: ${team.game_stats.shots}`
    } else {
        return ''
    }
}

export function formatStength(homeTeam, visitingTeam) {
    if (
        homeTeam.strength !== visitingTeam.strength ||
        homeTeam.strength === 5 ||
        visitingTeam.strength === 5
    ) {
        return null
    }

    return `${visitingTeam.strength} on ${homeTeam.strength}`
}

export function sortByClockTime(a, b) {
    let aClockTime = parseFloat(a.split(':').join('.'))
    let bClockTime = parseFloat(b.split(':').join('.'))
    if (aClockTime < bClockTime) {
        return -1
    }
    if (aClockTime > bClockTime) {
        return 1
    }
    return 0
}

export function formatBoxscore(team = {}) {
    const { goalies = [], skaters = [] } = team
    return [
        {
            title: 'Goalies',
            columns: [
                { title: 'GA', dataKey: 'goalsAgainst', tooltipText: 'Goals Against' },
                { title: 'SV', dataKey: 'saves', tooltipText: 'Saves' },
                { title: 'SA', dataKey: 'shotsAgainst', tooltipText: 'Shots Against' },
                { title: 'SV%', dataKey: 'savePercentage', tooltipText: 'Save Percentage' },
                { title: 'MIN', dataKey: 'minutes', tooltipText: 'Minutes Played' },
                {},
                {},
                {},
                {},
                {},
            ],
            data: goalies.map(goalie => {
                return {
                    name: getPlayerLinkWithPosition(goalie),
                    goalsAgainst: goalie.goals_against,
                    saves: goalie.saves,
                    shotsAgainst: goalie.shots_against,
                    savePercentage: goalie.save_percentage, //TODO - remove leading 0
                    minutes: goalie.minutes,
                }
            }),
        },
        {
            title: 'Skaters',
            columns: [
                { title: 'G', dataKey: 'goals', tooltipText: 'Goals' },
                { title: 'A', dataKey: 'assists', tooltipText: 'Assists' },
                { title: '+/-', dataKey: 'plusMinusRatio', tooltipText: 'Plus Minus Ratio' },
                { title: 'SOG', dataKey: 'shotsOnGoal', tooltipText: 'Shots on Goal' },
                { title: 'PIM', dataKey: 'penaltyInMinutes', tooltipText: 'Penalty Minutes' },
                { title: 'H', dataKey: 'hits', tooltipText: 'Hits' },
                { title: 'BLK', dataKey: 'blocks', tooltipText: 'Blocked Shots' },
                {
                    title: 'TOI',
                    dataKey: 'timeOnIce',
                    tooltipText: 'Time on Ice',
                    sortFunction: sortByClockTime,
                },
                { title: 'GVA', dataKey: 'turnoverGiveaways', tooltipText: 'Giveaways' },
                { title: 'TKA', dataKey: 'turnoverTakeaways', tooltipText: 'Takeaways' },
            ],
            data: skaters.map(skater => {
                return {
                    name: getPlayerLinkWithPosition(skater),
                    goals: skater.goals,
                    assists: skater.assists,
                    plusMinusRatio: skater.plus_minus_ratio,
                    shotsOnGoal: skater.shots_on_goal,
                    penaltyInMinutes: skater.penalty_in_minutes,
                    hits: skater.hits,
                    blocks: skater.blocks,
                    timeOnIce: skater.time_on_ice,
                    turnoverGiveaways: skater.turnover_giveaways,
                    turnoverTakeaways: skater.turnover_takeaways,
                }
            }),
        },
    ]
}
export function formatFaceoffs(team = {}) {
    const { skaters = [] } = team
    return [
        {
            title: 'Players',
            columns: [
                { title: 'FW', dataKey: 'faceoffsWon', tooltipText: 'Faceoffs Won' },
                { title: 'FL', dataKey: 'faceoffsLost', tooltipText: 'Faceoffs Lost' },
                { title: 'FO%', dataKey: 'faceoffsPercent', tooltipText: 'Faceoffs Percentage' },
                {},
                {},
                {},
                {},
                {},
                {},
                {},
            ],
            data: skaters
                ?.map(skater => {
                    if (skater.faceoffs === 0 && skater.faceoff_lost === 0) return null
                    return {
                        name: getPlayerLinkWithPosition(skater),
                        faceoffsWon: skater.faceoffs,
                        faceoffsLost: skater.faceoff_lost,
                        faceoffsPercent: calculatePercentage(
                            skater.faceoffs,
                            skater.faceoffs + skater.faceoff_lost,
                            1
                        ),
                    }
                })
                .filter(n => n), // filter out null values
        },
    ]
}

export function formatScratches(lineup = []) {
    const scratchedPlayers = lineup.filter(player => player.type === 'Scratched')
    const formattedScratchedPlayers = scratchedPlayers.map(player => {
        return `${player.first_name} ${player.last_name}`
    })
    return formattedScratchedPlayers.join(', ')
}
